import { checkResponse } from '@/utils/helpers';
import { IPrescriptOrderInfo } from '@/schemas/order';
import { request } from './request';
import { ApiConfigs, Response } from '../schemas/service';
import {
  ANSWER,
  IDrugInfoVO,
  IPrescriptionPreviewInfoVO,
  IPatientInfo,
  IDiseaseInfo,
  IPatientInfoResult,
  pregnancyBreastConfig,
  diseaseInfoResultConfig,
  IDiseaseVo,
  ISearchDrugVo,
  IUserAttrList,
  ISendPrescriptionResult,
  IPrescriptionDetailResult,
  IPrescriptionDetailCardInfo,
  IPrescriptionDetailPatientInfo,
  ISignUrls,
  IPrescriptionDrugDetail
} from '../schemas/prescription';

const api: ApiConfigs = {
  checkIsVerified: {
    // 校验用户是否实名
    url: '/health/api/v1/patientInfo/patientIsVerifiedCheck',
    method: 'GET'
  },
  checkPatientVerifyInfo: {
    // 校验患者实名信息是否正确
    url: '/health/api/v1/patientInfo/patientVerifyInfoCheck',
    method: 'POST'
  },
  getPatientInfo: {
    // 查询病史信息
    url: '/health/api/v1/patientInfo/queryPatientInfoByOrderId',
    method: 'GET'
  },
  updatePatientInfo: {
    // 更新病史信息
    url: '/health/api/v1/patientInfo/updatePatientInfo',
    method: 'PUT'
  },
  queryDisease: {
    url: '/health/api/v1/drug/queryDisease',
    method: 'GET'
  },
  queryDurg: {
    url: '/health/api/v1/drug/queryDrugs',
    // url:
    //   'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/drug/queryDrugs',
    method: 'GET'
  },
  queryPrescriptionDrugDetail: {
    // 处方药详情
    url: '/health/api/v1/drug/queryPrescriptionDrug',
    method: 'GET'
  },
  queryUserAttrList: {
    url: '/health/api/v1/userCustomAttr/queryUserAttrList',
    method: 'GET'
  },
  addUserAttrList: {
    url: '/health/api/v1/userCustomAttr/createUserCustomAttr',
    method: 'POST'
  },
  deleteUserAttrList: {
    url: '/health/api/v1/userCustomAttr/deleteUserCustomAttrById',
    method: 'DELETE'
  },
  getPrescription: {
    // 获取处方详情
    url: '/health/api/v1/drug/queryPrescription',
    method: 'GET'
  },
  createPrePrescription: {
    // 医生创建预开处方
    url: '/health/api/v1/drug/createPrePrescription',
    method: 'POST'
  },
  updatePrePrescription: {
    // 医生创建预开处方
    url: '/health/api/v1/drug/updatePrePrescription',
    method: 'POST'
  },
  submitPrescription: {
    // 提交处方
    url: '/health/api/v1/drug/createAndSendPrescription',
    method: 'GET'
  },
  sendPrescription: {
    // 发送处方
    url: '/health/api/v1/drug/sendPrePrescription',
    method: 'GET'
  },
  // 开方医师接单进群开方
  acceptPrescription: {
    url: '/health/api/v1/drug/acceptPrescription',
    method: 'GET'
  },
  bindPrescriptionDoctor: {
    url: '/health/api/v1/drug/bindPrescriberAndDesignating',
    method: 'GET'
  },
  // 处方详情页：根据处方id查询
  queryPrescriptionDetailInfo: {
    url: '/health/api/v1/drug/queryPrescription',
    method: 'GET'
  },
  // 处方详情页：点击撤回处方
  cancelPrescription: {
    url: '/health/api/v1/drug/cancelPrescription',
    method: 'GET'
  },
  sendPrescribingImNotice: {
    url: '/health/api/v1/drug/sendPrescribingImNotice',
    method: 'POST'
  },
  // 处方详情页：根据第三方订单id 查询第三方订单详情页面的跳转地址
  queryTpOrderUrl: {
    url: '/health/api/v1/drug/queryTpOrderDetailPage',
    method: 'GET'
  },
  prescriptionSuggest: {
    url: '/health/api/v1/drug/prescriptionSuggest',
    method: 'POST'
  }
};

const prescription = {
  async checkIsVerified(orderId) {
    try {
      const response: Response = await request(
        api.checkIsVerified,
        {
          orderId
        },
        undefined,
        {
          hideGlobalLoading: false
        }
      );
      const { result } = response;
      if (result != null) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async checkPatientVerifyInfo({
    patientIdCardNo,
    patientName
  }: {
    patientIdCardNo: string;
    patientName: string;
  }) {
    try {
      const response: Response = await request(
        api.checkPatientVerifyInfo,
        undefined,
        {
          patientIdCardNo,
          patientName
        }
      );
      const { result } = response;
      if (result !== null) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getPatientInfo(orderId) {
    try {
      const response: Response<IPatientInfoResult> = await request(
        api.getPatientInfo,
        { orderId },
        undefined,
        {
          hideGlobalLoading: false
        }
      );
      const { code, result } = response;
      const getDiseaseInfo = (obj) => {
        const resultObj = {};
        Object.keys(obj).forEach((key) => {
          let resultItem = {};
          if (key in diseaseInfoResultConfig) {
            if (key === 'nurseDesc') {
              if (obj[key] === null || obj[key] === '') {
                resultItem = {
                  answer: ANSWER.NO,
                  desc: undefined,
                  selectedDesc: undefined
                };
              } else {
                resultItem = {
                  answer: ANSWER.YES,
                  desc: pregnancyBreastConfig[obj[key]], // 后端返回数据以及用户在编辑时保存自定义数据
                  selectedDesc: pregnancyBreastConfig[obj[key]] // 保存用户选中的值以及作为接口请求的入参
                };
              }
            } else if (key !== 'nurseDesc') {
              if (obj[key] === null) {
                resultItem = { answer: ANSWER.NO, desc: [], selectedDesc: [] };
              } else {
                resultItem = {
                  answer: ANSWER.YES,
                  desc: obj[key],
                  selectedDesc: obj[key]
                };
              }
            }
          }
          resultObj[diseaseInfoResultConfig[key]] = resultItem;
        });
        return resultObj;
      };
      if (checkResponse(code, result)) {
        const target: {
          patientInfo: IPatientInfo;
          diseaseInfo: IDiseaseInfo | null;
        } = {
          diseaseInfo: getDiseaseInfo(result),
          patientInfo: {
            patientAge: result.patientAge,
            patientGender: result.patientGender,
            patientIdCardNo: result.patientIdCardNo,
            patientName: result.patientName,
            weight: result.weight,
            hasBeenModified: result.hasBeenModified,
            hasVerified: result.hasVerified,
            userId: result.userId
          }
        };
        return target;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async updatePatientInfo(params) {
    try {
      const response: Response = await request(
        api.updatePatientInfo,
        undefined,
        {
          ...params
        },
        {
          hideGlobalLoading: false
        }
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getDisease({
    diseaseName,
    source
  }: {
    diseaseName: string;
    source?: string;
  }) {
    try {
      const response: Response<IDiseaseVo[]> = await request(
        api.queryDisease,
        {
          diseaseName,
          source
        },
        undefined,
        {
          hideGlobalLoading: false
        }
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getDurgList({ drugName, drugType, source }) {
    try {
      const response: Response<ISearchDrugVo[]> = await request(
        api.queryDurg,
        {
          drugName,
          drugType,
          source
        },
        undefined,
        {
          hideGlobalLoading: false
        }
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getPrescriptionDrugDetail({
    drugId,
    source
  }: {
    drugId: string;
    source?: number;
  }) {
    try {
      const response: Response<IPrescriptionDrugDetail> = await request(
        api.queryPrescriptionDrugDetail,
        {
          drugId,
          source
        },
        undefined,
        {
          hideGlobalLoading: false
        }
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return {} as IPrescriptionDrugDetail;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getUserAttrList(attrTypeCode) {
    try {
      const response: Response<IUserAttrList[]> = await request(
        api.queryUserAttrList,
        {
          attrTypeCode
        },
        undefined,
        {
          hideGlobalLoading: false
        }
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async addUserAttrList({ attrTypeCode, attrValue }) {
    try {
      const response: Response<IUserAttrList[]> = await request(
        api.addUserAttrList,
        {
          attrTypeCode,
          attrValue
        },
        undefined,
        {
          hideGlobalLoading: false
        }
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async deleteUserAttrList({ id }) {
    try {
      const response: Response = await request(
        api.deleteUserAttrList,
        { id },
        undefined,
        {
          hideGlobalLoading: false
        }
      );
      const { code, result } = response;
      if (result !== null) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getPrescription(prescriptionId: string) {
    try {
      const {
        result,
        code
      }: Response<IPrescriptionPreviewInfoVO> = await request(
        api.getPrescription,
        {
          prescriptionId
        }
      );
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject();
    } catch (err) {
      return Promise.reject(err);
    }
  },
  async submitPrescription(prescriptionId: string, checkSign: number) {
    try {
      const { result, code }: Response = await request(
        api.submitPrescription,
        {
          prescriptionId,
          checkSign
        },
        undefined,
        {
          hideGlobalLoading: false,
          hideWeakNetworkToast: true
        }
      );
      if (checkResponse(code, result)) {
        return result;
      }
      return undefined;
    } catch (err) {
      return Promise.reject(err);
    }
  },
  async createPrePrescription(params) {
    try {
      const response: Response = await request(
        api.createPrePrescription,
        undefined,
        {
          ...params
        },
        {
          hideWeakNetworkToast: true,
          hideGlobalLoading: false
        }
      );

      const { result, code } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject(response);
    } catch (err) {
      return Promise.reject(err);
    }
  },
  async updatePrePrescription(params) {
    try {
      const { result, code }: Response = await request(
        api.updatePrePrescription,
        undefined,
        {
          ...params
        },
        {
          hideWeakNetworkToast: true,
          hideGlobalLoading: false
        }
      );
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject();
    } catch (err) {
      return Promise.reject(err);
    }
  },
  async sendPrescription(prescriptionId: string) {
    try {
      const { result, code }: Response<ISendPrescriptionResult> = await request(
        api.sendPrescription,
        {
          prescriptionId
        },
        undefined,
        {
          hideGlobalLoading: false,
          hideWeakNetworkToast: true
        }
      );
      if (checkResponse(code, result)) {
        return result;
      }
      return undefined;
    } catch (err) {
      return Promise.reject(err);
    }
  },
  async acceptPrescription(prescriptionId) {
    try {
      const { result, code }: Response<IPrescriptOrderInfo> = await request(
        api.acceptPrescription,
        {
          prescriptionId
        },
        {
          hideGlobalLoading: false
        }
      );
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async bindPrescriptionDoctor({ prescriptionId, prescriberId }) {
    try {
      const response: Response<boolean> = await request(
        api.bindPrescriptionDoctor,
        {
          prescriptionId,
          prescriberId
        }
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getPrescriptionDetailInfo(prescriptionId) {
    try {
      const response: Response<IPrescriptionDetailResult> = await request(
        api.queryPrescriptionDetailInfo,
        { prescriptionId },
        undefined,
        {
          hideGlobalLoading: false
        }
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        const target: {
          prescriptionDetailCardInfo: IPrescriptionDetailCardInfo;
          prescriptionDetailPatientInfo: IPrescriptionDetailPatientInfo;
          serviceDiseaseInfos: IDiseaseVo[];
          serviceDrugInfos: IDrugInfoVO[];
          commentContent: string;
          serviceSignUrls: ISignUrls;
          tpOrderId?: string;
          paymentStatus?: number;
          orderId: number;
          userId: number;
          doctorId: number;
          prescriberId: number;
        } = {
          prescriptionDetailCardInfo: {
            id: result.id,
            tpPrescriptionId: result.tpPrescriptionId,
            status: result.status,
            expireTime: result.expireTime,
            dealDesc: result.dealDesc
          },
          prescriptionDetailPatientInfo: {
            name: result.name,
            sex: result.sex,
            age: result.age,
            department: result.department,
            createTime: result.createTime,
            orderFileUrl: result.orderFileUrl
          },
          serviceDiseaseInfos: result.diseaseInfos,
          serviceDrugInfos: result.drugInfos,
          commentContent: result.comment,
          serviceSignUrls: {
            doctorSignUrl: result.doctorSignUrl,
            pharmacistSignUrl: result.pharmacistSignUrl,
            drugstoreSignUrl: result.drugstoreSignUrl,
            organizationSignUrl: result.organizationSignUrl
          },
          tpOrderId: result.tpOrderId,
          paymentStatus: result.paymentStatus,
          orderId: result.orderId,
          userId: result.userId,
          doctorId: result.doctorId,
          prescriberId: result.prescriberId
        };
        return target;
      }
      return Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async cancelPrescription(prescriptionId) {
    try {
      const response: Response = await request(
        api.cancelPrescription,
        {
          prescriptionId
        },
        undefined,
        { hideGlobalLoading: false }
      );
      const { result } = response;
      if (result) {
        return true;
      }
      return false;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async sendPrescribingImNotice(prescriptionId) {
    try {
      const response: Response = await request(api.sendPrescribingImNotice, {
        prescriptionId
      });
      const { result } = response;
      if (result) {
        return true;
      }
      return false;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getTpOrderUrl(source, tpOrderId) {
    try {
      const response: Response = await request(
        api.queryTpOrderUrl,
        {
          source,
          tpOrderId
        },
        undefined,
        { hideGlobalLoading: false }
      );
      const { result } = response;
      if (result) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async prescriptionSuggest(prescriptionId: string, suggestContents: string[]) {
    try {
      const { result, code }: Response<boolean> = await request(
        api.prescriptionSuggest,
        undefined,
        {
          prescriptionId,
          suggestContents
        }
      );
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.resolve(result);
    } catch (err) {
      return Promise.reject(err);
    }
  }
};

export default prescription;
